/** The document band's two charts - ADR-0212, package P9: where the library's documents are, and
 * when they arrived. Shared by Processing and Documents, which read the same counts.
 *
 * - **Stages** (`Funnel`): the server's own stage counts, in the server's order, each as the count
 *   of documents *in* that stage. They are not cumulative (a document is in one stage), so the bars
 *   do not claim a conversion rate. The stage that needs a person (`awaiting_review`) uses the amber
 *   and a stage that could not be read the muted red; the words say it too. A stage opens the place
 *   those documents are worked: Processing's tab, or the library.
 * - **Arrivals** (`MonthlyBars`, one series): documents received per week for twelve weeks, the
 *   server's `arrivals`. The current week has not ended, so it is drawn pale and dashed and its
 *   tooltip says so; it is never a real fall. A week is not a receipt, so a bar opens nothing.
 *
 * Both depend on the one counts read. Failed, they draw `ReadFailed` and no chart.
 */

import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useDocumentCounts } from "../../api/processing";
import type { DocumentCounts } from "../../api/processing";
import { Funnel, MonthlyBars } from "../../components/charts";
import type { BarMonth, FunnelStage } from "../../components/charts";
import { EmptyState } from "../../components/ui";
import { quantity, shortDate } from "../../lib/format";
import { keepSearch } from "../search";
import { useScreen } from "../useScreen";
import { BandCard } from "./BandCard";

const STAGE_WORD: Record<string, string> = {
  received: "Received",
  checked: "Checked",
  being_read: "Being read",
  awaiting_review: "To review",
  ready: "Accepted",
  could_not_be_read: "Refused",
  set_aside: "Set aside",
  replaced: "Replaced",
};

/** Where the documents of a stage are worked. */
const STAGE_PLACE: Record<string, { to: "/processing" | "/documents"; tab?: string }> = {
  received: { to: "/processing", tab: "in-progress" },
  being_read: { to: "/processing", tab: "in-progress" },
  awaiting_review: { to: "/processing" },
  could_not_be_read: { to: "/processing", tab: "refused" },
  checked: { to: "/documents" },
  ready: { to: "/documents" },
  set_aside: { to: "/documents" },
  replaced: { to: "/documents" },
};

export function stagesOf(counts: DocumentCounts): FunnelStage[] {
  return counts.stages.map((stage) => ({
    key: stage.stage,
    label: STAGE_WORD[stage.stage] ?? stage.stage.replace(/_/g, " "),
    value: stage.count,
    text: quantity(String(stage.count)),
    ...(stage.stage === "awaiting_review" ? { colour: "warn" as const, note: "Needs a person" } : {}),
    ...(stage.stage === "could_not_be_read" ? { colour: "crit" as const, note: "Could not be read" } : {}),
    receipt: stage.stage,
  }));
}

export function weeksOf(counts: DocumentCounts): BarMonth[] {
  const last = counts.arrivals.length - 1;
  return counts.arrivals.map((week, index) => ({
    key: week.week_start,
    label: shortDate(week.week_start).replace(/ \d{4}$/, ""),
    full: `Week of ${shortDate(week.week_start)}`,
    values: { received: week.received },
    ...(index === last ? { complete: false, missing: "week not over" } : {}),
  }));
}

const count = (value: number): string => quantity(String(Math.round(value)));

export function useDocumentCharts(): ReactNode[] {
  const counts = useDocumentCounts();
  const screen = useScreen();
  const navigate = useNavigate();
  const data = counts.data;

  const openStage = (stage: string) => {
    const place = STAGE_PLACE[stage];
    if (!place) return;
    void navigate({
      to: place.to,
      search: { ...keepSearch(screen), ...(place.tab ? { tab: place.tab } : {}) },
    });
  };
  const empty = data !== undefined && data.documents + data.superseded === 0;
  // A body that is not the shape the chart draws (an older server, a proxy's page) is no chart, not an
  // empty one: the arrivals are checked, and a drawing is never made from a guess.
  const hasStages = data !== undefined && Array.isArray(data.stages);
  const hasWeeks = data !== undefined && Array.isArray(data.arrivals);

  return [
    <BandCard key="stages" title="Documents by stage" unit="documents" reads={[counts]} testId="band-stages">
      {data && hasStages && !empty ? (
        <Funnel stages={stagesOf(data)} caption="Documents by stage" unit="Documents" onOpenReceipt={openStage} testId="band-funnel" />
      ) : (
        <EmptyState title="Nothing uploaded yet" compact />
      )}
    </BandCard>,
    <BandCard key="arrivals" title="Arrivals per week" unit="documents" reads={[counts]} testId="band-arrivals">
      {data && hasWeeks && !empty ? (
        <MonthlyBars
          months={weeksOf(data)}
          series={[{ key: "received", label: "Received", colour: "ramp1" }]}
          unit="documents"
          format={count}
          caption="Documents received per week"
          rowHeading="Week"
          height={200}
          testId="band-weeks"
        />
      ) : (
        <EmptyState title={data && !hasWeeks ? "Arrivals not available" : "Nothing uploaded yet"} compact />
      )}
    </BandCard>,
  ];
}
